import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

type Direction = 'up' | 'right' | 'down' | 'left';

interface Clue {
  row: number;
  col: number;
  direction: Direction;
  count: number;
}

interface FlechasLevel {
  size: number;
  difficulty: number;
  clues: Clue[];
  solution: number[][];
  seed: number;
}

export default function FlechasGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [level, setLevel] = useState<FlechasLevel | null>(null);
  const [userBoard, setUserBoard] = useState<number[][] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startTime] = useState(Date.now());
  const [moves, setMoves] = useState(0);
  const [solved, setSolved] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [undoStack, setUndoStack] = useState<number[][][]>([]);
  const [redoStack, setRedoStack] = useState<number[][][]>([]);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [modoAsistido, setModoAsistido] = useState(false);

  const dificultadOrden = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('flechas', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  useEffect(() => {
    loadPuzzle();
  }, [dificultadOrden]);

  const loadPuzzle = async (forceNew = false) => {
    try {
      setLoading(true);
      setError('');

      const urlSeed = !forceNew ? searchParams.get('seed') : null;
      const seedToUse = urlSeed ? parseInt(urlSeed) : undefined;

      const response = await api.get('/flechas/generate', {
        params: {
          dificultad: dificultadOrden,
          ...(seedToUse && { seed: seedToUse })
        }
      });

      const data: FlechasLevel = response.data.data;
      setLevel(data);

      // Initialize user board with -1 (undecided)
      const initialBoard = Array(data.size).fill(null).map(() =>
        Array(data.size).fill(-1)
      );
      setUserBoard(initialBoard);

      setMoves(0);
      setSolved(false);
      setShowVictory(false);
      setUndoStack([]);
      setRedoStack([]);

      const newUrl = `${window.location.pathname}?seed=${data.seed}`;
      window.history.replaceState({}, '', newUrl);
    } catch (err: any) {
      console.error('Error al cargar puzzle:', err);
      setError(err.response?.data?.error || 'Error al cargar el puzzle');
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (row: number, col: number, event: React.MouseEvent) => {
    if (!userBoard || !level || solved) return;

    // Check if it's a clue cell
    const isClue = level.clues.some(c => c.row === row && c.col === col);
    if (isClue) return;

    event.preventDefault();

    // Save for undo
    setUndoStack([...undoStack, JSON.parse(JSON.stringify(userBoard))]);
    setRedoStack([]);

    const newBoard = userBoard.map(r => [...r]);
    const current = newBoard[row][col];

    // Left click: toggle neutro ↔ negro
    if (event.button === 0) {
      if (current === -1 || current === -2) {
        newBoard[row][col] = 1; // Paint black
      } else {
        newBoard[row][col] = -1; // Clear to neutral
      }
    }
    // Right click: toggle X mark
    else if (event.button === 2) {
      if (current === -2) {
        newBoard[row][col] = -1; // Clear mark
      } else if (current === -1) {
        newBoard[row][col] = -2; // Mark with X
      }
      // If it's black (1), do nothing on right click
    }

    setUserBoard(newBoard);
    setMoves(moves + 1);

    // Auto-validate after every move (empty cells are considered white)
    setTimeout(() => checkSolution(newBoard), 300);
  };

  const checkSolution = async (board: number[][]) => {
    if (!level) return;


    try {
      // Convert board: -1 (empty) and -2 (marked X) become 0 (white) for validation
      const validationBoard = board.map(row =>
        row.map(cell => (cell === -1 || cell === -2) ? 0 : cell)
      );


      const response = await api.post('/flechas/validate', {
        board: validationBoard,
        clues: level.clues,
        size: level.size
      });


      const { valid, errors } = response.data.data;

      if (valid) {
        setSolved(true);
        const tiempo = Math.floor((Date.now() - startTime) / 1000);
        await saveProgress(tiempo);
        setShowVictory(true);
      } else {
        // No mostrar errores durante el juego - solo validar en silencio
      }
    } catch (err: any) {
      console.error('💥 Error validando:', err);
      setError('Error al validar la solución');
    }
  };

  const saveProgress = async (tiempo: number) => {
    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: level?.seed || 0,
        tiempo,
        movimientos: moves,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo,
        movimientos: moves,
        completado: true,
        datos_juego: {
          tablero_id: level?.seed || 0,
          size: level?.size || 0,
        },
      });
    } catch (error) {
      console.error('Error guardando progreso:', error);
    }
  };

  const handleUndo = () => {
    if (undoStack.length === 0 || !userBoard) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack([...redoStack, JSON.parse(JSON.stringify(userBoard))]);
    setUserBoard(prev);
    setUndoStack(undoStack.slice(0, -1));
  };

  const handleRedo = () => {
    if (redoStack.length === 0 || !userBoard) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack([...undoStack, JSON.parse(JSON.stringify(userBoard))]);
    setUserBoard(next);
    setRedoStack(redoStack.slice(0, -1));
  };

  const handleHint = async () => {
    if (!userBoard || !level) return;

    try {
      // Convert board for hint request
      const validationBoard = userBoard.map(row =>
        row.map(cell => cell === -2 ? 0 : cell)
      );

      const response = await api.post('/flechas/hint', {
        board: validationBoard,
        solution: level.solution,
        size: level.size
      });

      const { hint } = response.data.data;
      if (hint) {
        setUndoStack([...undoStack, JSON.parse(JSON.stringify(userBoard))]);
        const newBoard = userBoard.map(row => [...row]);
        newBoard[hint.row][hint.col] = hint.value;
        setUserBoard(newBoard);
        setMoves(moves + 1);
      }
    } catch (err: any) {
      console.error('Error obteniendo pista:', err);
    }
  };

  const showSolution = () => {
    if (!level || !window.confirm('¿Estás seguro? Esto mostrará la solución completa.')) return;
    setUserBoard(level.solution.map(row => [...row]));
    setSolved(true);
  };

  const handleReset = () => {
    if (!level || !window.confirm('¿Estás seguro de que quieres reiniciar?')) return;
    const initialBoard = Array(level.size).fill(null).map(() =>
      Array(level.size).fill(-1)
    );
    setUserBoard(initialBoard);
    setMoves(0);
    setSolved(false);
    setShowVictory(false);
    setUndoStack([]);
    setRedoStack([]);
  };

  const getArrowSymbol = (direction: Direction): string => {
    const arrows: Record<Direction, string> = {
      'up': '↑',
      'right': '→',
      'down': '↓',
      'left': '←'
    };
    return arrows[direction];
  };

  // ─── Modo asistido ────────────────────────────────────────────────────────
  function calcularErrores(board: number[][], lv: FlechasLevel): Set<string> {
    const errores = new Set<string>();
    const { size, clues } = lv;

    // 1. Negras adyacentes ortogonalmente
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (board[r][c] !== 1) continue;
        if (r + 1 < size && board[r + 1][c] === 1) {
          errores.add(`${r},${c}`); errores.add(`${r + 1},${c}`);
        }
        if (c + 1 < size && board[r][c + 1] === 1) {
          errores.add(`${r},${c}`); errores.add(`${r},${c + 1}`);
        }
      }
    }

    // 2. Pistas de flecha
    const deltas: Record<Direction, [number, number]> = {
      up: [-1, 0], down: [1, 0], left: [0, -1], right: [0, 1]
    };
    for (const clue of clues) {
      const [dr, dc] = deltas[clue.direction];
      let r = clue.row + dr;
      let c = clue.col + dc;
      let blackCount = 0;
      let hasUndecided = false;
      const blackCells: [number, number][] = [];

      while (r >= 0 && r < size && c >= 0 && c < size) {
        if (board[r][c] === 1) { blackCount++; blackCells.push([r, c]); }
        else if (board[r][c] === -1) hasUndecided = true;
        r += dr; c += dc;
      }

      // Demasiadas negras ya → error definitivo
      if (blackCount > clue.count) {
        errores.add(`${clue.row},${clue.col}`);
        blackCells.forEach(([br, bc]) => errores.add(`${br},${bc}`));
      }
      // Línea completa y conteo incorrecto → error
      else if (!hasUndecided && blackCount !== clue.count) {
        errores.add(`${clue.row},${clue.col}`);
      }
    }

    return errores;
  }

  const getClueAtPosition = (row: number, col: number): Clue | null => {
    if (!level) return null;
    return level.clues.find(c => c.row === row && c.col === col) || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Cargando...</div>
      </div>
    );
  }

  if (!level || !userBoard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center text-white space-y-4">
          <div className="text-5xl">🔄</div>
          <div className="text-xl font-semibold">Generando puzzle...</div>
          <p className="text-white/60 text-sm">A veces se necesitan varios intentos para encontrar un puzzle con solución única.</p>
          <button
            onClick={() => setForceReload(prev => prev + 1)}
            className="mt-4 px-6 py-2 bg-white/20 hover:bg-white/30 rounded-xl font-semibold transition-all"
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  const size = level.size;

  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  return (
    <div className="h-full overflow-hidden bg-gradient-to-br from-purple-900 to-indigo-900 flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🏹 Flechas</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => navigate(`/juegos/flechas/${d.orden}`)}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                dificultadOrden === d.orden
                  ? 'bg-white text-purple-900'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} {d.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          {/* Undo */}
          <button onClick={handleUndo} disabled={undoStack.length === 0} title="Deshacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </button>
          {/* Redo */}
          <button onClick={handleRedo} disabled={redoStack.length === 0} title="Rehacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
            </svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          {/* Hint */}
          <button onClick={handleHint} disabled={solved} title="Pista"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </button>
          {/* Solution */}
          <button onClick={showSolution} disabled={solved} title="Ver solucion"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          {/* Reset */}
          <button onClick={handleReset} title="Reiniciar"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {/* New board */}
          <button onClick={() => loadPuzzle(true)} title="Nuevo tablero"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
          {/* Cómo se juega */}
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-3 mt-2 p-2 bg-red-500/20 border border-red-400/50 text-red-200 rounded text-sm">
          {error}
        </div>
      )}

      {/* Board Area */}
      <div
        className="flechas-board flex-1 min-h-0 grid gap-[2px] bg-gray-400"
        style={{
          gridTemplateColumns: `repeat(${size}, 1fr)`,
          gridTemplateRows: `repeat(${size}, 1fr)`,
        }}
      >
          {(() => {
            const errores = modoAsistido ? calcularErrores(userBoard, level) : new Set<string>();
            return userBoard.map((row, r) =>
              row.map((cell, c) => {
                const clue = getClueAtPosition(r, c);
                const isClue = clue !== null;
                const isClickable = !isClue && !solved;
                const hasError = errores.has(`${r},${c}`);

                let bgColor = '#ffffff';
                let color = '#1f2937';
                let borderColor = '#d1d5db';
                let content = '';
                let cursor = isClickable ? 'pointer' : 'default';

                if (isClue) {
                  bgColor = hasError ? '#fecaca' : '#fde68a';
                  color = hasError ? '#991b1b' : '#92400e';
                  borderColor = hasError ? '#f87171' : '#b45309';
                  content = `${getArrowSymbol(clue.direction)}${clue.count}`;
                } else if (cell === 1) {
                  bgColor = hasError ? '#7f1d1d' : '#1e293b';
                  color = '#ffffff';
                  borderColor = hasError ? '#ef4444' : '#0f172a';
                  content = '';
                } else if (cell === -2) {
                  color = '#9ca3af';
                  content = '✕';
                }

                const fontSize = `calc(min((100vw - 10px) / ${size}, (100vh - 90px) / ${size}) * 0.5)`;

                return (
                  <button
                    key={`${r}-${c}`}
                    onClick={(e) => handleCellClick(r, c, e)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      handleCellClick(r, c, e as any);
                    }}
                    disabled={!isClickable}
                    className="flex items-center justify-center font-bold border transition-all"
                    style={{ fontSize, backgroundColor: bgColor, color, borderColor, cursor }}
                  >
                    {content}
                  </button>
                );
              })
            );
          })()}
      </div>

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Flechas"
        gameIcon="🏹"
        objective="Pinta celdas de negro o blanco de forma que cada casilla con número y flecha indique exactamente cuántas celdas negras hay en esa dirección hasta el borde del tablero."
        rules={[
          'Cada casilla con flecha y número es blanca: indica cuántas celdas negras hay en esa dirección hasta el borde.',
          'No puede haber dos celdas negras adyacentes ortogonalmente (sí en diagonal).',
          'Todas las celdas blancas deben formar una única área continua ortogonalmente.',
          'Los valores de las celdas negras son irrelevantes.',
        ]}
        howToPlay="Toca una celda para ponerla negra. Toca de nuevo para marcarla con ✕ (descartada). Toca una tercera vez para limpiarla. Las casillas con flecha son fijas."
        author="Otto Janko"
      />

      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{
          tiempo: Math.floor((Date.now() - startTime) / 1000),
          movimientos: moves,
        }}
      />
    </div>
  );
}
